import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Command, CommandInput, CommandList } from "@/components/ui/command";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getAutoDirection } from "@/lib/direction";

import { GlobalSearchChatsSection } from "./GlobalSearchChatsSection";
import { GlobalSearchFooter } from "./GlobalSearchFooter";
import { GlobalSearchGroupList } from "./GlobalSearchGroupList";
import { GlobalSearchHeader } from "./GlobalSearchHeader";
import { GlobalSearchServerStatus } from "./GlobalSearchServerStatus";
import { useGlobalSearchHighlight } from "./hooks/use-global-search-highlight";
import { useGlobalSearchResults } from "./hooks/use-global-search-results";
import { useGlobalSearchSelect } from "./hooks/use-global-search-select";
import type { IGlobalSearchContentProps } from "./types";

// NOTE: NSAI-6589 — the palette body. Mounted only while the dialog is open,
// so every query behind it (organizations, FAQ, /search/global) runs only then.
// Filtering is off because every source ranks its own results.
const GlobalSearchContentComponent = ({
  onClose,
}: IGlobalSearchContentProps): JSX.Element => {
  const { t } = useTranslation("globalSearch");
  const [query, setQuery] = useState("");
  // NOTE: NSAI-6750 — a picked result clears the query before closing. The
  // body normally unmounts on close, but its exit animation keeps it mounted
  // until `animationend`, and a result that opens a new tab (the docs action)
  // hides this one mid-animation — reopening then reused the stale query.
  const handleClose = useCallback((): void => {
    setQuery("");
    onClose();
  }, [onClose]);
  const {
    leadingGroups,
    trailingGroups,
    chats,
    server,
    isSearching,
    hasOtherResults,
  } = useGlobalSearchResults({ query, onClose: handleClose });
  const handleSelectItem = useGlobalSearchSelect({ onClose: handleClose });
  const { rootRef, value, onValueChange, onKeyDown, onPointerMoveCapture } =
    useGlobalSearchHighlight({
      query,
      leadingGroups,
      trailingGroups,
      chats: chats.chats,
      semanticHits: chats.semanticHits,
    });

  return (
    <Command
      ref={rootRef}
      shouldFilter={false}
      value={value}
      onValueChange={onValueChange}
      onKeyDown={onKeyDown}
      onPointerMoveCapture={onPointerMoveCapture}
    >
      <GlobalSearchHeader
        title={t("title")}
        description={t("description")}
        closeLabel={t("close_label")}
      />
      <div className="flex flex-col gap-4 px-4">
        <CommandInput
          autoFocus
          dir={getAutoDirection({ isDisabled: !query })}
          inputMode="text"
          value={query}
          onValueChange={setQuery}
          placeholder={t("placeholder")}
          aria-label={t("trigger_label")}
          wrapperClassName="flex items-center bg-search-input-background border border-input px-3 rounded-lg focus-within:border-ring focus-within:ring-1 focus-within:ring-offset-0 focus-within:ring-ring"
          className="flex h-11 w-full rounded-lg bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
          iconClassName="me-2 h-4 w-4 shrink-0 opacity-50"
          data-testid="search-chats-input"
        />
      </div>
      <CommandList className="max-h-none" data-testid="search-chats-list">
        {/* NOTE: NSAI-6763 — Radix's `display: table` content wrapper grows
            to its widest row, which pushed the "…", the badges and RTL titles
            past the dialog edge. Constrained, every row is as wide as the list. */}
        <ScrollArea
          isViewportContentConstrained
          className="p-4"
          viewportClassName="max-h-96 h-96"
          data-testid="search-chats-scroll"
          viewportRef={chats.setScrollContainer}
        >
          <GlobalSearchGroupList
            groups={leadingGroups}
            onSelectItem={handleSelectItem}
          />
          <GlobalSearchChatsSection
            state={chats}
            isSearching={isSearching}
            hasOtherResults={hasOtherResults}
          />
          <GlobalSearchGroupList
            groups={trailingGroups}
            onSelectItem={handleSelectItem}
          />
          <GlobalSearchServerStatus
            isLoading={server.isLoading}
            isError={server.isError}
            onRetry={server.onRetry}
          />
        </ScrollArea>
      </CommandList>
      <GlobalSearchFooter />
    </Command>
  );
};

export const GlobalSearchContent = memo(GlobalSearchContentComponent);
